import React from 'react';

export const isIndependenceThemeActive = () => {
  const now = new Date();
  const start = new Date('2026-08-01T00:00:00');
  const end = new Date('2026-08-20T23:59:59');
  return now >= start && now <= end;
};

const WAVE_A =
  'M0,60 C120,20 240,100 360,60 C480,20 600,100 720,60 C840,20 960,100 1080,60 C1200,20 1320,100 1440,60 L1440,120 L0,120 Z';
const WAVE_B =
  'M0,70 C180,30 300,95 480,65 C660,35 780,105 960,70 C1140,35 1300,95 1440,65 L1440,120 L0,120 Z';

const PARTICLES = [
  { left: '6%', size: 7, color: '#FF9933', delay: 0, dur: 11 },
  { left: '14%', size: 5, color: '#138808', delay: 2.5, dur: 13 },
  { left: '23%', size: 8, color: '#FF9933', delay: 5, dur: 12 },
  { left: '31%', size: 4, color: '#1e40af', delay: 1.2, dur: 14 },
  { left: '40%', size: 6, color: '#138808', delay: 3.8, dur: 11.5 },
  { left: '49%', size: 5, color: '#FF9933', delay: 6.5, dur: 12.5 },
  { left: '58%', size: 7, color: '#138808', delay: 0.8, dur: 13.5 },
  { left: '66%', size: 4, color: '#FF9933', delay: 4.2, dur: 11 },
  { left: '74%', size: 6, color: '#1e40af', delay: 7.2, dur: 14 },
  { left: '82%', size: 5, color: '#138808', delay: 2, dur: 12 },
  { left: '90%', size: 8, color: '#FF9933', delay: 5.6, dur: 13 },
  { left: '96%', size: 4, color: '#138808', delay: 8, dur: 11.8 }
];

// 3D floating Tricolour wave background — auto-disabled after 20 Aug 2026
export const TricolorWaveBackground = () => {
  if (!isIndependenceThemeActive()) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: -1, perspective: '1000px' }}
      data-testid="tricolor-wave-background"
      aria-hidden="true"
    >
      <style>{`
        @keyframes waveSlide {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes waveFloat3d {
          0%, 100% { transform: rotateX(40deg) translateY(0px) translateZ(0); }
          50% { transform: rotateX(30deg) translateY(-18px) translateZ(40px); }
        }
        @keyframes waveFloat3dAlt {
          0%, 100% { transform: rotateX(-32deg) translateY(0px) scale(1); }
          50% { transform: rotateX(-42deg) translateY(16px) scale(1.02); }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.08); }
        }
        @keyframes chakraSpinBg {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes particleRise {
          0% { transform: translateY(105vh) scale(0.6); opacity: 0; }
          12% { opacity: 0.85; }
          85% { opacity: 0.5; }
          100% { transform: translateY(-8vh) scale(1.15); opacity: 0; }
        }
        @keyframes lightSweep {
          0% { transform: translateX(-70%) skewX(-18deg); }
          100% { transform: translateX(170%) skewX(-18deg); }
        }
        .tri-wave-track { display: flex; width: 200%; animation: waveSlide linear infinite; }
        .tri-wave-svg { width: 50%; height: 100%; flex-shrink: 0; }
        .tri-particle {
          position: absolute; bottom: -20px; border-radius: 9999px;
          animation: particleRise linear infinite;
          filter: blur(0.5px);
        }
      `}</style>

      {/* Soft tricolor ambient glows */}
      <div
        className="absolute -top-28 -left-28 w-[60vw] h-[50vh] rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(255,153,51,0.30), transparent 68%)', animation: 'glowPulse 6s ease-in-out infinite' }}
      ></div>
      <div
        className="absolute top-1/3 right-0 w-[45vw] h-[45vh] rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(30,64,175,0.15), transparent 70%)', animation: 'glowPulse 7.5s ease-in-out infinite 1.5s' }}
      ></div>
      <div
        className="absolute -bottom-28 -right-28 w-[60vw] h-[50vh] rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(19,136,8,0.30), transparent 68%)', animation: 'glowPulse 6.5s ease-in-out infinite 3s' }}
      ></div>

      {/* Giant rotating Ashoka Chakra watermark */}
      <svg
        viewBox="0 0 100 100"
        className="absolute left-1/2 top-1/2 w-[54vh] h-[54vh] opacity-[0.07]"
        style={{ animation: 'chakraSpinBg 60s linear infinite' }}
      >
        <circle cx="50" cy="50" r="46" fill="none" stroke="#1e40af" strokeWidth="3" />
        <circle cx="50" cy="50" r="6" fill="#1e40af" />
        {Array.from({ length: 24 }, (_, i) => {
          const angle = (i * 15 * Math.PI) / 180;
          return (
            <line
              key={i}
              x1={50 + 6 * Math.cos(angle)}
              y1={50 + 6 * Math.sin(angle)}
              x2={50 + 44 * Math.cos(angle)}
              y2={50 + 44 * Math.sin(angle)}
              stroke="#1e40af"
              strokeWidth="1.6"
            />
          );
        })}
      </svg>

      {/* Diagonal light sweep */}
      <div
        className="absolute top-0 bottom-0 w-[28vw]"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
          animation: 'lightSweep 9s ease-in-out infinite'
        }}
      ></div>

      {/* Top saffron waves — two parallax layers */}
      <div
        className="absolute -top-8 left-0 right-0 h-48"
        style={{ animation: 'waveFloat3d 7s ease-in-out infinite', transformStyle: 'preserve-3d' }}
      >
        <div className="tri-wave-track h-full" style={{ animationDuration: '13s' }}>
          {[0, 1].map((i) => (
            <svg key={i} className="tri-wave-svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d={WAVE_A} fill="rgba(255,153,51,0.34)" />
            </svg>
          ))}
        </div>
      </div>
      <div
        className="absolute -top-2 left-0 right-0 h-36"
        style={{ animation: 'waveFloat3d 8.5s ease-in-out infinite 0.8s', transformStyle: 'preserve-3d' }}
      >
        <div className="tri-wave-track h-full" style={{ animationDuration: '22s', animationDirection: 'reverse' }}>
          {[0, 1].map((i) => (
            <svg key={i} className="tri-wave-svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d={WAVE_B} fill="rgba(234,124,20,0.18)" />
            </svg>
          ))}
        </div>
      </div>

      {/* Middle floating white/blue ripple */}
      <div
        className="absolute top-[44%] left-0 right-0 h-32 opacity-70"
        style={{ animation: 'waveFloat3dAlt 8s ease-in-out infinite', transformStyle: 'preserve-3d' }}
      >
        <div className="tri-wave-track h-full" style={{ animationDuration: '19s', animationDirection: 'reverse' }}>
          {[0, 1].map((i) => (
            <svg key={i} className="tri-wave-svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d={WAVE_B} fill="rgba(255,255,255,0.6)" />
              <path d={WAVE_A} fill="rgba(30,64,175,0.10)" transform="translate(-220,-10)" />
            </svg>
          ))}
        </div>
      </div>

      {/* Bottom green waves — two parallax layers (flipped) */}
      <div
        className="absolute -bottom-8 left-0 right-0 h-52"
        style={{ animation: 'waveFloat3d 7.5s ease-in-out infinite 1s', transform: 'scaleY(-1)', transformStyle: 'preserve-3d' }}
      >
        <div className="tri-wave-track h-full" style={{ animationDuration: '15s' }}>
          {[0, 1].map((i) => (
            <svg key={i} className="tri-wave-svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d={WAVE_A} fill="rgba(19,136,8,0.34)" />
            </svg>
          ))}
        </div>
      </div>
      <div
        className="absolute -bottom-2 left-0 right-0 h-40"
        style={{ animation: 'waveFloat3d 9s ease-in-out infinite 2s', transform: 'scaleY(-1)', transformStyle: 'preserve-3d' }}
      >
        <div className="tri-wave-track h-full" style={{ animationDuration: '24s', animationDirection: 'reverse' }}>
          {[0, 1].map((i) => (
            <svg key={i} className="tri-wave-svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d={WAVE_B} fill="rgba(21,128,61,0.18)" />
            </svg>
          ))}
        </div>
      </div>

      {/* Floating tricolor particles */}
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className="tri-particle"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`
          }}
        ></span>
      ))}
    </div>
  );
};

export default TricolorWaveBackground;
